import { LuzmoDashboardComponent } from '@luzmo/react-embed';
import {
  ArrowDropDown as ArrowDropDownIcon,
  CreateOutlined as CreateOutlinedIcon,
  Delete as DeleteIcon,
  NoteAddOutlined as NoteAddOutlinedIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import luzmo from 'common/src/constants/luzmo';
import { BASE, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';

import ContainerLoading from 'portal/src/components/common/ContainerLoading';
import FormErrorMessage from 'portal/src/components/common/forms/FormErrorMessage';
import ConfirmDeleteDialog from 'portal/src/components/common/tables/ConfirmDeleteDialog';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useLuzmo from 'portal/src/providers/useLuzmo';
import luzmoStyles from 'portal/src/theme/luzmoStyles';

import useAuth from '../../auth/useAuth';
import useSnackbarHandler from '../../utils/Snackbar/useSnackbarHandler';

export default function DashboardViewer() {
  const { hasReadOnlyAccess } = useAuth();
  const navigate = useNavigate();
  const { id: routeDashboardId } = useParams();
  const snackbarHandler = useSnackbarHandler();
  const {
    credentials,
    customDashboards: dashboards,
    deleteCustomDashboard,
    error,
    isLoading,
    refreshData,
  } = useLuzmo();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isNewMode, setIsNewMode] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isDisabled = hasReadOnlyAccess() || isLoading;

  useEffect(() => {
    if (
      (dashboards.length > 0 && !routeDashboardId && !isNewMode) ||
      routeDashboardId === NESTED_ROUTES.CUSTOM_DASHBOARDS.MY_DASHBOARDS
    ) {
      navigate(`${BASE.CUSTOM_DASHBOARDS}/${dashboards[0].id}`);
    }
  }, [routeDashboardId, dashboards, navigate, isNewMode]);

  const enterViewMode = () => {
    setIsNewMode(false);
    setEditMode(false);
  };

  const enterNewMode = () => {
    setIsNewMode(true);
    setEditMode(false);
  };

  const enterEditMode = () => {
    setIsNewMode(false);
    setEditMode(true);
  };

  const handleDashboardSelection = (id: string) => {
    setAnchorEl(null);

    if (id !== routeDashboardId) {
      navigate(`${BASE.CUSTOM_DASHBOARDS}/${id}`);
      enterViewMode();
    }
  };

  const handleDashboardDeletion = async () => {
    if (!routeDashboardId) {
      setShowDeleteDialog(false);
      return;
    }

    const wasDeleted = await deleteCustomDashboard(routeDashboardId);
    setShowDeleteDialog(false);

    if (wasDeleted) {
      snackbarHandler.success('Dashboard deleted');
      navigate(BASE.CUSTOM_DASHBOARDS);
      return;
    }

    snackbarHandler.error(MESSAGES.DASHBOARD.DELETION_ERROR);
  };

  const handleSaveOrClose = async () => {
    const existingDashboardIds = new Set(
      dashboards.map((dashboard) => dashboard.id),
    );
    const { customDashboards: refreshedDashboards } = await refreshData();

    if (isNewMode) {
      const newDashboard = refreshedDashboards.find(
        (dashboard) => !existingDashboardIds.has(dashboard.id),
      );

      if (newDashboard) {
        navigate(`${BASE.CUSTOM_DASHBOARDS}/${newDashboard.id}`);
      }
    }

    enterViewMode();
  };

  const dashboardDisplay = useMemo(() => {
    const resolvedDashboardId = isNewMode ? 'new' : routeDashboardId;
    const mode = isEditMode || isNewMode ? 'editFull' : 'view';

    return routeDashboardId || isNewMode ? (
      <LuzmoDashboardComponent
        dashboardId={resolvedDashboardId}
        authKey={credentials.key}
        authToken={credentials.token}
        appServer={luzmo.appServer}
        apiHost={luzmo.apiHost}
        editMode={mode}
        key={`${resolvedDashboardId}-${mode}`}
        {...luzmoStyles}
      />
    ) : undefined;
  }, [
    routeDashboardId,
    isNewMode,
    credentials.key,
    credentials.token,
    isEditMode,
  ]);

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <FormErrorMessage
          actionLabel="Refresh"
          message="Something went wrong. Please try again later."
          handleAction={refreshData}
        />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" minHeight="95vh" gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button
          variant="outlined"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<ArrowDropDownIcon />}
          disabled={isLoading || isEditMode || isNewMode}
        >
          Select Dashboard
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          {!isNewMode && !isEditMode && (
            <Button
              color="primary"
              disabled={isDisabled}
              endIcon={<NoteAddOutlinedIcon />}
              onClick={() => enterNewMode()}
              variant="contained"
            >
              New
            </Button>
          )}
          {routeDashboardId && !isNewMode && !isEditMode && (
            <>
              <Button
                color="primary"
                disabled={isDisabled}
                endIcon={<CreateOutlinedIcon />}
                onClick={enterEditMode}
                variant="contained"
              >
                Edit
              </Button>
              <Button
                color="error"
                disabled={isDisabled}
                endIcon={<DeleteIcon />}
                onClick={() => setShowDeleteDialog(true)}
                variant="contained"
              >
                Delete
              </Button>
            </>
          )}
          {(isNewMode || isEditMode) && (
            <>
              <Button
                color="primary"
                disabled={isDisabled}
                endIcon={<SaveIcon />}
                onClick={handleSaveOrClose}
                variant="contained"
              >
                Save
              </Button>
              <Button
                color="primary"
                disabled={isDisabled}
                onClick={handleSaveOrClose}
                variant="outlined"
              >
                Close
              </Button>
            </>
          )}
        </Stack>
        <Menu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          open={!!anchorEl}
          slotProps={{ list: { sx: { width: 'fit-content' } } }}
        >
          {isLoading && dashboards.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              px={2}
            >
              <ContainerLoading additionalLoadingText="dashboard list" />
            </Box>
          ) : dashboards.length === 0 ? (
            <MenuItem disabled>
              No available dashboards, please create a new one.
            </MenuItem>
          ) : (
            dashboards.map((d) => (
              <MenuItem
                key={d.id}
                sx={{ py: 1 }}
                onClick={() => handleDashboardSelection(d.id)}
                selected={routeDashboardId === d.id}
              >
                {d.name}
              </MenuItem>
            ))
          )}
        </Menu>
      </Stack>
      {isLoading ? (
        <ContainerLoading
          additionalLoadingText="custom dashboard"
          marginTop={18}
          spinnerSize={30}
          spinnerOnTop
        />
      ) : dashboardDisplay ? (
        dashboardDisplay
      ) : (
        !routeDashboardId &&
        !isNewMode && <Typography mt={3}>No dashboard selected.</Typography>
      )}
      <ConfirmDeleteDialog
        isLoading={isLoading}
        itemName="dashboard"
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDashboardDeletion}
        showDialog={showDeleteDialog}
      />
    </Box>
  );
}
